/**
 * 筒灯/射灯场景（用于识别 JSON 与手动新增筒灯共用，保持短 token）
 * 若增删 id，请同步后端 `nemo-light-backend/src/services/geminiService.ts` 内 `DOWNLIGHT_CONTEXT_ENUM_FOR_PROMPT`。
 */
export const DOWNLIGHT_CONTEXT_VALUES = [
  // 居住
  'entry_dust',
  'entry_art',
  'living_table',
  'living_wall_wave',
  'living_plant_back',
  'dining_focus',
  'bedroom_avoid_glare',
  'bedroom_foot',
  // 商业零售
  'fashion_mannequin',
  'fashion_shelf_wash',
  'jewelry_sparkle',
  'fb_private',
  'supermarket_fresh',
  // 展陈
  'museum_protect',
  'museum_spread',
  'museum_track',
  // 办公公共
  'office_table',
  'office_wall_wash',
  'office_ugr10',
  'hotel_lobby_high',
  // 室外
  'out_wall_wash',
  'out_tree_in',
  'out_tree_out',
  'out_fountain_under',
  // 医疗康养
  'medical_night_guide',
  'medical_asym_wall',
  'surgery_shadowless',
  'rehab_circadian',
  // 枢纽
  'hub_ugr10',
  'hub_security_uniform',
  'garage_carpet',
  'garage_pillar',
  // 影音娱乐
  'theater_step',
  'bar_bottle_up',
  'bar_table',
  // 展厅发布会
  'auto_highlight',
  'tracking_spot',
  // 宗教精神
  'sacred_dome',
  'sacred_statue',
  // 兜底
  'generic',
] as const;

export type DownlightContext = (typeof DOWNLIGHT_CONTEXT_VALUES)[number];

/** 光束形态（用于提示词与默认参数推断） */
export type BeamProfile = 'narrow' | 'medium' | 'wide' | 'oval' | 'asym_wall' | 'shadowless' | 'track';

export function normalizeDownlightContext(raw: unknown): DownlightContext {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if ((DOWNLIGHT_CONTEXT_VALUES as readonly string[]).includes(s)) return s as DownlightContext;
  return 'generic';
}

export function normalizeBeamProfile(raw: unknown): BeamProfile | undefined {
  const s = String(raw ?? '').trim().toLowerCase();
  if (
    s === 'narrow' ||
    s === 'medium' ||
    s === 'wide' ||
    s === 'oval' ||
    s === 'asym_wall' ||
    s === 'shadowless' ||
    s === 'track'
  )
    return s;
  return undefined;
}

export function defaultBeamProfileForContext(ctx: DownlightContext): BeamProfile {
  switch (ctx) {
    case 'entry_art':
    case 'fashion_mannequin':
    case 'bar_table':
    case 'auto_highlight':
      return 'narrow';
    case 'fashion_shelf_wash':
      return 'oval';
    case 'medical_asym_wall':
    case 'office_wall_wash':
    case 'out_wall_wash':
      return 'asym_wall';
    case 'surgery_shadowless':
      return 'shadowless';
    case 'museum_track':
    case 'tracking_spot':
      return 'track';
    case 'office_table':
    case 'living_table':
    case 'dining_focus':
      return 'medium';
    case 'hotel_lobby_high':
      return 'narrow';
    default:
      return 'medium';
  }
}

/** 为手动新增筒灯给一个稳定的默认 beamAngle（度） */
export function defaultBeamAngleForProfile(p: BeamProfile): number {
  switch (p) {
    case 'narrow':
      return 15;
    case 'medium':
      return 24;
    case 'wide':
      return 60;
    case 'oval':
      return 36;
    case 'asym_wall':
      return 45;
    case 'shadowless':
      return 60;
    case 'track':
      return 24;
    default:
      return 24;
  }
}

export const DOWNLIGHT_CONTEXT_GROUPS: { title: string; ids: DownlightContext[] }[] = [
  {
    title: '居住',
    ids: [
      'entry_dust',
      'entry_art',
      'living_table',
      'living_wall_wave',
      'living_plant_back',
      'dining_focus',
      'bedroom_avoid_glare',
      'bedroom_foot',
    ],
  },
  { title: '商业零售', ids: ['fashion_mannequin', 'fashion_shelf_wash', 'jewelry_sparkle', 'fb_private', 'supermarket_fresh'] },
  { title: '展陈博物馆', ids: ['museum_protect', 'museum_spread', 'museum_track'] },
  { title: '办公公共', ids: ['office_table', 'office_wall_wash', 'office_ugr10', 'hotel_lobby_high'] },
  { title: '室外景观', ids: ['out_wall_wash', 'out_tree_in', 'out_tree_out', 'out_fountain_under'] },
  { title: '医疗康养', ids: ['medical_night_guide', 'medical_asym_wall', 'surgery_shadowless', 'rehab_circadian'] },
  { title: '交通枢纽/车库', ids: ['hub_ugr10', 'hub_security_uniform', 'garage_carpet', 'garage_pillar'] },
  { title: '影音娱乐', ids: ['theater_step', 'bar_bottle_up', 'bar_table'] },
  { title: '展厅发布会', ids: ['auto_highlight', 'tracking_spot'] },
  { title: '宗教精神', ids: ['sacred_dome', 'sacred_statue'] },
  { title: '通用', ids: ['generic'] },
];

export const DOWNLIGHT_CONTEXT_LABEL_ZH: Record<DownlightContext, string> = {
  entry_dust: '玄关落尘区',
  entry_art: '玄关艺术端景',
  living_table: '客厅茶几中心',
  living_wall_wave: '墙面波浪弧光',
  living_plant_back: '绿植逆光剪影',
  dining_focus: '餐桌焦点照明',
  bedroom_avoid_glare: '卧室避光（不直射眼）',
  bedroom_foot: '卧室足元灯（低位）',
  fashion_mannequin: '服装模特焦点',
  fashion_shelf_wash: '货架洗墙（椭圆）',
  jewelry_sparkle: '珠宝火彩（高对比）',
  fb_private: '餐饮私密隔断',
  supermarket_fresh: '卖场生鲜照明',
  museum_protect: '文物保护（低UV）',
  museum_spread: '拉伸光斑（横幅）',
  museum_track: '重点追踪（变焦）',
  office_table: '会议室桌面照度',
  office_wall_wash: '办公墙面洗墙',
  office_ugr10: '深防眩（UGR<10）',
  hotel_lobby_high: '挑高大堂下照',
  out_wall_wash: '室外建筑洗墙',
  out_tree_in: '树木内照',
  out_tree_out: '树木外照（月光感）',
  out_fountain_under: '水下喷泉',
  medical_night_guide: '康养夜间引导',
  medical_asym_wall: '非对称洗墙（避眩）',
  surgery_shadowless: '无影阵列照明',
  rehab_circadian: '节律照明',
  hub_ugr10: '枢纽深防眩',
  hub_security_uniform: '安检均匀无死角',
  garage_carpet: '车位入户光毯',
  garage_pillar: '车库立柱轮廓',
  theater_step: '影院台阶脚步',
  bar_bottle_up: '酒瓶背光（自下而上）',
  bar_table: '桌面聚光（极窄束）',
  auto_highlight: '车漆高光条',
  tracking_spot: '跟踪射灯（动态）',
  sacred_dome: '穹顶洗光',
  sacred_statue: '神像重点照明',
  generic: '通用筒灯/射灯',
};

